import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { Stack, Text } from '@etg/wings';
import { Notification, variants } from '@eti/components';
import { useProperty, useSearch, useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import type { SortType } from '@eti/schema-types';
import { TripType as SchemaTripType } from '@eti/schema-types';
import type { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { useFreeCancellationDaysAheadNotification } from '../../../../common/custom-hooks/freeCancellationDaysAheadNotification';
import { getFlightsExcludingSponsoredTripTags } from '../../utils/resultUtils';
import { resetAllFilters } from '../search-filters/utils/searchFilters';
import ValidWithVoucherSwitch from '../ValidWithVoucherSwitch';
import QuickSortButtonBarV1 from '../v1/QuickSortButtonBar';
import DifferentChargingCurrencyNotification from '../../../../widgets/different-charging-currency-notification/DifferentChargingCurrencyNotification';
import type { SearchFormValues } from '../../../../common/search-form-panel/SearchFormNewContainer';
import type {
  NearbyDatePricesType,
  SearchOnResultPageQuickSortPricesType,
  TripsType,
} from '../../models';
import type { NearbyPricesWithoutSelectedTripVersionEnumTypesValues } from './nearbyDatesPrices/components/NearbyDatesPrices';
import NoResults from './NoResults';
import QuickSortButtonBarV2 from './quickSorting/QuickSortButtonBar';
import SearchResultsLoader from './SearchResultsLoader';
import { useResultPageContext } from './contexts/ResultPageContext';
import FlightResultSkeleton from './skeleton/FlightResultSkeleton';
import { NUMBER_OF_RESULTS } from './skeleton/ResultSkeleton';
import NearbyDatesPrices, {
  NearbyPricesWithoutSelectedTripVersionEnumTypes,
} from './nearbyDatesPrices/components/NearbyDatesPrices';
import type { QuickSortPricesType } from './model';
import ResultsTripCardsSection from './ResultTripCardsSection';
import { getFlightWhenInTripCampaigns, isAnyRecommendedFlights } from './utils/trip';

const differentChargingCurrencyNotificationStyles = css`
  padding-block-end: 8px;
`;

const sortingNotificationStyles = css`
  color: #737373;
  font-size: 0.875rem;
  line-height: 150%;
`;

interface ResultsSectionProps {
  flightResultsWrapperRef: MutableRefObject<null>;
  isNearbyDatesLoading: boolean;
  isSortingInformationEnabled?: boolean;
  nearbyDatePrices: NearbyDatePricesType;
  searchFormWatchRef: MutableRefObject<UseFormWatch<SearchFormValues> | null>;
  setFetchMoreResults: Dispatch<SetStateAction<boolean>>;
  setValueRef: MutableRefObject<UseFormSetValue<SearchFormValues> | null>;
}

const ResultsSection = ({
  flightResultsWrapperRef,
  isNearbyDatesLoading,
  isSortingInformationEnabled = false,
  nearbyDatePrices,
  searchFormWatchRef,
  setFetchMoreResults,
  setValueRef,
}: ResultsSectionProps) => {
  const {
    handleSearch,
    isSearchLoading,
    isSelfServiceRebooking,
    toggleSearchPanel,
    selectedTrip,
    searchData,
    shouldShowResultsLoader,
    handleShowValidWithVoucher,
    handleSort,
    setIsMobileSortDialogOpen,
    isMobileSortDialogOpen,
  } = useResultPageContext();
  const { p } = useProperty();
  const { t } = useTranslation();
  const { currentSearchData, setSearchData } = useSearch();
  const freeCancellationDaysAheadNotification = useFreeCancellationDaysAheadNotification(
    searchData?.origin?.departuredAt,
  );
  const quickSortVersion = p('Result.Page.QuickSort.Version');
  const isSkeletonEnabled = p('Result.Skeleton.Enabled');
  const isNearbyDatesPricesEnabled = p('Result.Page.NearbyPrices.Enabled');
  const isFreeCancellationEnabled = p('BookingFlow.FreeCancellation.Enable');
  const isResultPageQuickFiltersEnabled = p('Result.Page.QuickFilters.Enabled');
  const isValidWithVoucherEnabled = p('Result.SelfServiceRebooking.ValidWithVoucherSwitch.Enable');
  const isDifferentChargingCurrencyNotificationEnabled = p(
    'IbeClient.DifferentChargingCurrencyNotification.Enabled',
  );
  const nearbyPricesWithoutSelectedTripVersion: NearbyPricesWithoutSelectedTripVersionEnumTypesValues =
    p('Result.Page.NearbyPrices.WithoutSelectedTrip.Version');
  const sortingLegalNotice = t('Result.Sorting.Notice');
  const notificationText = freeCancellationDaysAheadNotification
    ? t('BookingFlow.FreeCancellation.DaysAhead.Text')
    : t('BookingFlow.FreeCancellation.Text');
  const notificationTestId = freeCancellationDaysAheadNotification
    ? 'freeCancellationInformationDaysAhead-search-result'
    : 'freeCancellationInformation-search-result';
  const { filteredFlightsCount, flightsCount, flights, tripCampaigns } = {
    ...searchData,
  };
  const { selectedSortType } = currentSearchData || {};
  const tripsExcludingSponsored: TripsType =
    isAnyRecommendedFlights(flights, selectedSortType) &&
    flights &&
    flights[0] &&
    getFlightWhenInTripCampaigns(flights[0], tripCampaigns)
      ? getFlightsExcludingSponsoredTripTags(flights.slice(1, flights.length), selectedSortType)
      : getFlightsExcludingSponsoredTripTags(flights, selectedSortType);

  const isTripTypeAllowedToNearbyDatesPrices = (tripType?: SchemaTripType) => {
    return (
      Boolean(tripType === SchemaTripType.OneWay) || Boolean(tripType === SchemaTripType.Return)
    );
  };
  const shouldShowNearbyDatesPrices =
    isNearbyDatesPricesEnabled &&
    isTripTypeAllowedToNearbyDatesPrices(tripsExcludingSponsored[0]?.type);
  const shouldShowNearbyDatesWithoutSelectedTripAtTop =
    shouldShowNearbyDatesPrices &&
    !selectedTrip &&
    nearbyPricesWithoutSelectedTripVersion === NearbyPricesWithoutSelectedTripVersionEnumTypes.A;
  const shouldShowNearbyDatesWithoutSelectedTripBelowQuicksort =
    shouldShowNearbyDatesPrices &&
    !selectedTrip &&
    nearbyPricesWithoutSelectedTripVersion === NearbyPricesWithoutSelectedTripVersionEnumTypes.B;

  const onFilter = (filter: any) => {
    setSearchData!({ filters: { ...currentSearchData?.filters, ...filter } });
  };

  const transformQuickSortPrices = (quickSortPrices: SearchOnResultPageQuickSortPricesType) =>
    Object.fromEntries(Object.entries(quickSortPrices).map(([key, item]) => [key, item.value]));

  const quickSortPrices = (
    searchData && searchData.quickSortPrices
      ? transformQuickSortPrices(searchData.quickSortPrices)
      : null
  ) as QuickSortPricesType;

  const nearbyDatesPricesComponent = (
    <NearbyDatesPrices
      handleSearch={handleSearch}
      hasSelectedTrip={selectedTrip}
      isLoading={isNearbyDatesLoading}
      nearbyDatePrices={nearbyDatePrices}
      searchFormWatchRef={searchFormWatchRef}
      setValueRef={setValueRef}
    />
  );

  return (
    <Stack spacing={8}>
      {isDifferentChargingCurrencyNotificationEnabled && (
        <DifferentChargingCurrencyNotification
          className={differentChargingCurrencyNotificationStyles}
        />
      )}

      {/* Nearby dates at the top */}
      {shouldShowNearbyDatesWithoutSelectedTripAtTop && nearbyDatesPricesComponent}

      {isResultPageQuickFiltersEnabled &&
        quickSortPrices &&
        (quickSortVersion === 2 ? (
          <QuickSortButtonBarV2
            isOpen={isMobileSortDialogOpen}
            onDismiss={setIsMobileSortDialogOpen}
            selectedSortType={selectedSortType as SortType}
            sortPrices={quickSortPrices}
          />
        ) : (
          <div data-testid="Quicksort-button-bar-container-v1">
            <QuickSortButtonBarV1
              onSort={handleSort}
              selectedSortType={selectedSortType as SortType}
              sortPrices={quickSortPrices}
            />
          </div>
        ))}

      {isSortingInformationEnabled && (
        <div className={sortingNotificationStyles}>
          <Text asHtml>{sortingLegalNotice}</Text>
        </div>
      )}

      {/* Nearby dates below Quick sort */}
      {shouldShowNearbyDatesWithoutSelectedTripBelowQuicksort && nearbyDatesPricesComponent}

      {isSelfServiceRebooking && isValidWithVoucherEnabled && (
        <ValidWithVoucherSwitch
          onShowValidWithVoucher={handleShowValidWithVoucher}
          shouldShowValidWithVoucher={searchData?.validWithVoucher}
        />
      )}

      {(freeCancellationDaysAheadNotification || isFreeCancellationEnabled) && (
        <Notification dataTestId={notificationTestId} variant={variants.CONFIRMATION}>
          <Text asHtml>{notificationText}</Text>
        </Notification>
      )}

      {shouldShowResultsLoader && (
        <SearchResultsLoader shouldShowResultsLoader={shouldShowResultsLoader} />
      )}

      {isSkeletonEnabled && shouldShowResultsLoader && (
        <Stack>
          {[...Array(NUMBER_OF_RESULTS)].map((_, index) => (
            <FlightResultSkeleton key={index} />
          ))}
        </Stack>
      )}

      {!shouldShowResultsLoader && (
        <ResultsTripCardsSection
          flightResultsWrapperRef={flightResultsWrapperRef}
          isNearbyDatesLoading={isNearbyDatesLoading}
          nearbyDatePrices={nearbyDatePrices}
          searchFormWatchRef={searchFormWatchRef}
          setValueRef={setValueRef}
          shouldShowNearbyDatesPrices={shouldShowNearbyDatesPrices}
          tripsExcludingSponsored={tripsExcludingSponsored}
        />
      )}

      <NoResults
        filteredFlightsCount={filteredFlightsCount}
        flightResults={flights}
        flightsCount={flightsCount}
        handleFilter={onFilter}
        isSearchLoading={isSearchLoading}
        resetAllFilters={resetAllFilters}
        selectedFilters={currentSearchData?.filters}
        setFetchMoreResults={setFetchMoreResults}
        toggleSearchPanel={toggleSearchPanel}
      />
    </Stack>
  );
};

export default ResultsSection;
